import React, { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';

// Page transition wrapper
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

// Animated section component
function AnimatedSection({ children, className, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

function Concepts() {
  const navigate = useNavigate();

  // Other concepts
  const otherConcepts = [
    {
      id: 'fan-activation',
      title: 'Concert Pre-Show Activation',
      subtitle: 'Deepening Fan Connection Before the Show',
      category: 'Live Event Design',
      status: 'Concept',
      color: 'pink',
      image: '/images/concepts/fan-activation/preshow-mockup.png',
      description: `Reimagining the concert pre-show experience as an immersive journey
        that builds anticipation and deepens fan connection before they enter the venue.`,
      features: [
        'Themed environments reflecting artist\'s world',
        'Interactive games and challenges',
        'Exclusive content unlocks and rewards',
        'Community-building activations',
        'Photo moments and social integration',
      ],
      inspiration: 'The magic of the moments before a show - transforming waiting into anticipation.',
    },
    {
      id: 'immersive-dining',
      title: 'Theatrical Dining Experience',
      subtitle: 'Where Food Meets Narrative',
      category: 'Immersive Dining',
      status: 'Concept',
      color: 'warm',
      image: '/images/concepts/immersive-dining/dining-mockup.png',
      description: `A multi-course dining experience where each dish is part of a larger
        narrative journey. Guests don't just eat, they participate in an unfolding story
        through taste, environment, and performance.`,
      features: [
        'Narrative-driven menu progression',
        'Transforming environments between courses',
        'Live theatrical elements and performers',
        'Multi-sensory pairing experiences',
        'Personalized story branches',
      ],
      inspiration: 'Combining the intimacy of fine dining with the wonder of immersive theater.',
    },
  ];

  return (
    <motion.div
      className="concepts-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.5 }}
    >
      {/* Page Hero - Playful */}
      <section className="page-hero playful">
        <div className="container">
          <motion.div
            className="page-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="page-label">Portfolio</span>
            <h1 className="page-title playful-title">
              <span className="title-line title-italic">Concepts</span>
              <span className="title-line">& Ideas</span>
            </h1>
            <p className="page-subtitle">
              Dream projects and speculative designs exploring the <em>future</em> of immersive entertainment.
            </p>
          </motion.div>
        </div>
        <div className="hero-decoration" />
      </section>

      {/* Intro */}
      <section className="concepts-intro">
        <div className="container">
          <AnimatedSection className="intro-split">
            <div className="intro-visual">
              <div className="intro-icon-cluster">
                <span className="icon-diamond">◇</span>
                <span className="icon-diamond">◇</span>
                <span className="icon-diamond">◇</span>
                <span className="icon-star">✦</span>
              </div>
            </div>
            <div className="intro-statement">
              <p className="statement-main">
                The best experiential designers don't just execute existing ideas - they imagine new ones.
              </p>
              <p className="statement-sub">
                This section showcases my approach to experience design through concept work:
                mockups, mood boards, and written proposals for immersive projects that push boundaries.
              </p>
              <p className="statement-sub">
                These pieces demonstrate my ability to vision beyond execution, imagining new
                possibilities in themed entertainment, fan experiences, and interactive storytelling.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          FEATURED CONCEPT: OLIVIA RODRIGO - BRIEF OVERVIEW
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="featured-concept-preview">
        <div className="container">
          <AnimatedSection className="featured-preview-content">
            <div
              className="featured-preview-clickable"
              onClick={() => navigate('/concepts/olivia-rodrigo')}
              style={{ cursor: 'pointer' }}
            >
            <div className="featured-preview-image">
              <img
                src="/images/concepts/olivia-rodrigo/room1/Mockup-RoomOverview.png"
                alt="Drivers License immersive experience mockup"
              />
              <div className="featured-preview-badge">Featured Concept</div>
            </div>

            <div className="featured-preview-text">
              <div className="concept-meta">
                <span className="concept-category">Fan Activation</span>
                <span className="concept-status">Speculative Design</span>
              </div>
              <h2>&ldquo;Drivers License: The Anniversary Experience&rdquo;</h2>
              <p className="featured-preview-subtitle">A Five-Year Celebration of the Song That Changed Everything</p>
              <p className="featured-preview-description">
                A speculative pop-up experience concept celebrating Olivia Rodrigo's breakthrough single.
                The design translates the emotional universe of the song (nostalgia, heartbreak, first loves) into
                a physical environment with three immersive rooms, each pulling from iconic moments
                in her career.
              </p>
              <div className="featured-preview-highlights">
                <span>3 Immersive Rooms</span>
                <span>Revenue & Merch Strategy</span>
                <span>Full Business Framework</span>
              </div>
              <span className="btn btn-primary">
                View Full Concept →
              </span>
            </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          OTHER CONCEPTS
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="concepts-showcase other-concepts">
        <div className="container">
          <AnimatedSection className="other-concepts-header">
            <h2>More <em>Concepts</em></h2>
          </AnimatedSection>

          {otherConcepts.map((concept, index) => (
            <AnimatedSection
              key={concept.id}
              className={`concept-card-compact concept-${concept.color}`}
              delay={index * 0.1}
            >
              <div className="concept-compact-layout">
                <div className="concept-compact-image">
                  <motion.div
                    className="concept-thumb-wrapper"
                    whileHover={{ scale: 1.03 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    {concept.image ? (
                      <img
                        src={concept.image}
                        alt={`${concept.title} concept visualization`}
                        className="concept-thumb"
                      />
                    ) : (
                      <div className="concept-thumb-placeholder">
                        <span>Concept Visualization</span>
                      </div>
                    )}
                  </motion.div>
                </div>

                <div className="concept-compact-content">
                  <div className="concept-meta">
                    <span className="concept-category">{concept.category}</span>
                    <span className="concept-status">{concept.status}</span>
                  </div>
                  <h3 className="concept-title">{concept.title}</h3>
                  <p className="concept-subtitle">{concept.subtitle}</p>
                  <p className="concept-description">{concept.description}</p>

                  <div className="concept-features-inline">
                    {concept.features.slice(0, 3).map((feature, i) => (
                      <span key={i} className="feature-tag">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* CTA - Playful */}
      <section className="page-cta playful">
        <div className="container">
          <AnimatedSection className="cta-content-playful">
            <h2>Let's Bring Ideas to <em>Life</em></h2>
            <p>
              Interested in collaborating on experiential projects? I'd love to
              hear about your vision.
            </p>
            <div className="cta-links">
              <a href="mailto:sami@samidenicola.com" className="btn btn-primary">
                Get in Touch
              </a>
              <Link to="/experiential/about" className="btn btn-primary">
                About Sami
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </motion.div>
  );
}

export default Concepts;
